import { inject, Injectable } from '@angular/core';
import { ApiService } from './app-api.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuditTrailRecordDTO } from '../interface/audit-trail.interface';
import { map, Observable } from 'rxjs';
import { ToastService } from './app-toast.service';

@Injectable({
  providedIn: 'root',
})
export class AppCommonService {
  newIssueFormControllers!: FormGroup;
  existingIssueFormControllers!: FormGroup;
  private readonly appService = inject(ApiService);

  constructor(
    private readonly fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly toastService: ToastService,
  ) {
    this.updateIssueForm();
  }

  getUserDetails() {
    return this.apiService.getCurrentUserDetails();
  }

  createISOTimeZone(date: string) {
    let startDate = new Date(date);
    let yyyy = startDate.getFullYear();
    let mm = String(startDate.getMonth() + 1).padStart(2, '0');
    let dd = String(startDate.getDate()).padStart(2, '0');
    let hh = String(startDate.getHours()).padStart(2, '0');
    let min = String(startDate.getMinutes()).padStart(2, '0');
    let ss = String(startDate.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
  }

  updateIssueForm() {
    this.newIssueFormControllers = this.fb.group({
      issueID: ['', Validators.required],
      description: ['', Validators.required],
      idIssueCategory: [null],
      dateCreation: [''],
      dateModification: [''],
    });
  }

  /** Formats an ISO date string as M/D/YYYY h:mm:ss AM/PM */
  formatDisplayDateTime(date: string): string {
    const d = new Date(date);
    const mm = d.getMonth() + 1;
    const dd = d.getDate();
    const yyyy = d.getFullYear();
    let hours = d.getHours();
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${mm}/${dd}/${yyyy} ${hours}:${min}:${ss} ${period}`;
  }

  /** Formats an ISO date string as M/D/YYYY h:mm:ss AM/PM */
  formatDisplayDate(date: string): string {
    if (!date) return '';
    const d = new Date(date);
    const mm = d.getMonth() + 1;
    const dd = d.getDate();
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  getAuditTrailById(auditID: number): Observable<AuditTrailRecordDTO[]> {
    return this.appService.getAuditTrailById(auditID).pipe(
      map((response) => {
        const res = response?.result;
        const data: AuditTrailRecordDTO[] = res.map((item: any, index: number) => ({
          Index: index + 1,
          'Field Name': item.fieldName,
          'Modified By': item.userName,
          DateTime: this.formatDisplayDateTime(item.auditDateTime),
          'Old Value': item.oldValue,
          'New Value': item.newValue,
        }));
        return data;
      }),
    );
  }

  canIssueBeCancelled(): boolean {
    const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
    const isEditor = sessionStorage.getItem('isEditor') === 'true';
    if (!isAdmin && !isEditor) {
      return false;
    }
    if (isEditor || isAdmin) {
      return true;
    }
    return true;
  }

  getSpDocLink(value: string | null | undefined) {
    const storedValue = value?.trim() ?? '';
    if (!storedValue) {
      return { displayText: '', title: '', url: null, hasSeparator: false, isClickable: false };
    }
    const separatorIndex = storedValue.indexOf('#');
    if (separatorIndex === -1) {
      return {
        displayText: storedValue,
        title: storedValue,
        url: null,
        hasSeparator: false,
        isClickable: true,
      };
    }
    const displayText = storedValue.slice(0, separatorIndex).trim();
    const destination = storedValue.slice(separatorIndex + 1).trim();
    const url = this.normalizeExternalUrl(destination);
    return {
      displayText: displayText || destination,
      title: destination,
      url,
      hasSeparator: true,
      isClickable: Boolean(url),
    };
  }

  private normalizeExternalUrl(value: string): string | null {
    if (!value || (/^[a-z][a-z\d+.-]*:/i.test(value) && !/^https?:\/\//i.test(value))) {
      return null;
    }
    const candidate = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(candidate);
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }
}
